<template>
  <v-container full-height fluid>
    <v-row no-gutters v-if="showSearch">
      <v-col cols="12">
        <defect-notices-filter v-model="filterParams" @closesearch="hideSearchBar()" @searching="searchInitiated()"></defect-notices-filter>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="xs-12 md-6">
        <v-card class="ma-4">
          <v-card-title>
            Unactioned Notices
            <v-spacer></v-spacer>
            <v-icon @click="openAssignedDefects()" class="title-icon" :color="assignedColor" large>person</v-icon>
            <v-chip color="primary" class="mr-5 body-2 font-weight-medium" outlined>{{ countDefectsToAction }} </v-chip>
            <v-btn color="primary" @click="openReceive()">Add EDN</v-btn>
          </v-card-title>
          <v-data-table
            disable-pagination
            :items="defectsToAction"
            :headers="unactionedHeaders"
            :sort-by="unactionedOptions.sortBy"
            :sort-desc="unactionedOptions.sortDescending"
            :loading="$wait.is(['loading pending defects notices'])"
            hide-default-footer
          >
            <v-progress-linear v-slot:progress color="blue" indeterminate></v-progress-linear>
            <template v-slot:no-data>
              <v-layout row align-center>
                <v-flex>There are no EDN's to action</v-flex>
              </v-layout>
            </template>
            <template v-slot:item="props">
              <tr @click="openAction(props.item)" :class="props.item.isActioningOverdue ? 'secondary' : ''">
                <td>{{ props.item.receivedDate && moment(props.item.receivedDate) | formatDate }}</td>
                <td>{{ props.item.ednNumber }}</td>
                <td>{{ props.item.incidentNumber }}</td>
                <td>{{ props.item.sourceSystem }}</td>
                <td>{{ props.item.unitNumber }} <v-icon small v-if="props.item.unitOnRoad === true">mdi-truck</v-icon><v-icon small v-else-if="props.item.unitOnRoad === false">mdi-store</v-icon></td>
                <td>{{ props.item.receivedTotal }}</td>
                <td>{{ moment(props.item.createdOn) | formatDateTime }}</td>
                <td class="shrink actions">
                  <v-btn class="overline" text small icon v-if="props.item.defects.some(d => d.photoBlobUri)">
                    <v-icon>mdi-image</v-icon>
                  </v-btn>
                  <v-btn class="overline" text small icon v-if="props.item.defects.length > 0 && !props.item.defects.every(d => d.status !== 'Submitted')">
                    <v-icon>assignment_late</v-icon>
                  </v-btn>
                  <v-btn text small icon v-if="props.item.sourceSystem === 'GateKeeper' && !props.item.startedAction" @click="submitStart($event, props.item.id)" title="Start work on this EDN">
                    <v-icon>add_alarm</v-icon>
                  </v-btn>
                  <span class="overline" v-if="props.item.sourceSystem === 'GateKeeper' && props.item.startedAction">{{ times[props.item.id] }}</span>
                  <v-btn text small icon v-if="userIsFleetOrWorkshopManager && props.item.garageLocationId != props.item.unitGarageId" @click="openMove($event, props.item)" title="Move this EDN to home garage">
                    <v-icon>send</v-icon>
                  </v-btn>
                  <v-menu>
                    <template v-slot:activator="{ on }">
                      <v-btn height="24" icon text small v-on="on" @click="handleClick">
                        <v-icon :color="noticeAssignedColor(props.item.defects)">person</v-icon>
                      </v-btn>
                    </template>
                    <v-card class="pb-5">
                      <v-card-title class="assignment-headline">
                        Assigned Defects
                      </v-card-title>
                      <v-card-text class="assignment" v-for="d in props.item.defects" :key="d.id"> {{ d.lineNumber }} - {{ d.assignees.map(a => a.displayName).join(', ') }} </v-card-text>
                    </v-card>
                  </v-menu>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
      <v-col class="xs-12 md-6">
        <v-card class="ma-4">
          <v-card-title>Unscheduled Notices</v-card-title>
          <v-data-table
            disable-pagination
            :items="defectsToSchedule"
            :headers="schedulingHeaders"
            :sort-by="unscheduledOptions.sortBy"
            :sort-desc="unscheduledOptions.sortDescending"
            :loading="$wait.is(['loading pending defects notices'])"
            hide-default-footer
          >
            <v-progress-linear v-slot:progress color="blue" indeterminate></v-progress-linear>
            <template v-slot:no-data>
              <v-layout row align-center>
                <v-flex>There are no EDN's to schedule</v-flex>
              </v-layout>
            </template>
            <template v-slot:item="props">
              <tr @click="openSchedule(props.item)" :class="props.item.isSchedulingOverdue ? 'secondary' : ''">
                <td>{{ moment(props.item.receivedDate) | formatDate }}</td>
                <td>{{ props.item.ednNumber }}</td>
                <td>{{ props.item.incidentNumber }}</td>
                <td>{{ props.item.unitNumber }}</td>
                <td>{{ props.item.receivedTotal }}</td>
                <td>{{ props.item.completedOnReceipt }}</td>
                <td class="shrink actions">
                  <v-btn text small icon v-if="userIsFleetOrWorkshopManager && props.item.garageLocationId != props.item.unitGarageId" @click="openMove($event, props.item)" title="Move this EDN to home garage">
                    <v-icon>send</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-card class="mx-4">
          <v-card-title class="clickable" @click="reviewExpanded = !reviewExpanded">
            For Review
            <v-spacer></v-spacer>
            <v-icon v-if="reviewExpanded">arrow_drop_up</v-icon>
            <v-icon v-else>arrow_drop_down</v-icon>
          </v-card-title>
          <v-expand-transition>
            <v-data-table
              v-show="reviewExpanded"
              :items="reviewDefects"
              :headers="reviewHeaders"
              :loading="$wait.is(['loading review defects'])"
              :footer-props.sync="reviewFooterProps"
              :server-items-length="totalReviewDefects"
              @update:page="refreshReviewDefects"
              @update:sort-by="refreshReviewDefects"
              @update:sort-desc="refreshReviewDefects"
              @update:items-per-page="refreshReviewDefects"
              :options.sync="reviewOptions"
            >
              <v-progress-linear v-slot:progress color="blue" indeterminate></v-progress-linear>
              <template v-slot:item="props">
                <tr @click="openAcceptDefect(props.item)">
                  <td>{{ moment(props.item.receivedDate) | formatDate }}</td>
                  <td>{{ props.item.ednNumber }}/{{ props.item.lineNumber }}</td>
                  <td>{{ props.item.description }}</td>
                  <td>{{ props.item.statusText }}</td>
                  <td class="shrink">{{ props.item.unitNumber }}</td>
                  <td>{{ props.item.unitName }}</td>
                  <td class="shrink text-center">
                    <v-menu v-if="props.item.hivisStatus && props.item.hivisStatus.status !== 'NotApplicable'">
                      <template v-slot:activator="{ on }">
                        <v-btn height="24" icon text small v-on="on" @click="handleClick">
                          <v-icon :color="getHivisColor(props.item.hivisStatus)">{{ getHivisIcon(props.item.hivisStatus) }}</v-icon>
                        </v-btn>
                      </template>
                      <v-card class="pb-3">
                        <v-card-title class="hivis-headline">
                          <template v-if="props.item.hivisStatus.status === 'HistoricallyAcknowledged' || props.item.hivisStatus.status === 'HistoricallyNotAcknowledged'">
                            HIVIS History ({{ props.item.hivisStatus.historicalDetails.reduce((sum, d) => sum + d.assignees.length, 0) }} Previous)
                          </template>
                          <template v-else>
                            HIVIS Status ({{ props.item.hivisStatus.totalConfirmed }}/{{ props.item.hivisStatus.totalRequired }})
                          </template>
                        </v-card-title>
                        <v-card-text class="hivis-detail" v-for="detail in props.item.hivisStatus.details" :key="detail.lineNumber">
                          <div v-for="(assignee, idx) in detail.assignees" :key="idx" class="ml-3">
                            <v-icon small :color="assignee.acknowledged ? 'green' : 'red'">{{ assignee.acknowledged ? 'mdi-check' : 'mdi-close' }}</v-icon>
                            {{ assignee.displayName }}
                            <span v-if="assignee.acknowledged" class="grey--text text--darken-1"> - {{ moment(assignee.acknowledgedAt) | formatDateTime }}</span>
                            <span v-else class="grey--text text--darken-1"> - Not acknowledged</span>
                          </div>
                        </v-card-text>
                        <template v-if="props.item.hivisStatus.historicalDetails && props.item.hivisStatus.historicalDetails.length > 0">
                          <v-divider v-if="props.item.hivisStatus.details.length > 0" class="mt-3 mb-2"></v-divider>
                          <div class="px-4 pt-2 pb-1 font-weight-medium grey--text text--darken-2">
                            <v-icon small color="grey darken-2" class="mr-1">mdi-history</v-icon>
                            Previous Acknowledgements
                          </div>
                          <v-card-text class="hivis-detail pt-1" v-for="detail in props.item.hivisStatus.historicalDetails" :key="'hist-' + detail.lineNumber">
                            <div v-for="(assignee, idx) in detail.assignees" :key="idx" class="ml-3">
                              <v-icon small :color="assignee.acknowledged ? 'green' : 'red'">{{ assignee.acknowledged ? 'mdi-check' : 'mdi-close' }}</v-icon>
                              <span class="grey--text text--darken-1">
                                {{ assignee.displayName }}
                                <template v-if="assignee.acknowledged"> - {{ moment(assignee.acknowledgedAt) | formatDateTime }}</template>
                                <template v-else-if="assignee.removedAt"> - {{ moment(assignee.removedAt) | formatDateTime }}</template>
                                <template v-else> - Not acknowledged</template>
                              </span>
                            </div>
                          </v-card-text>
                        </template>
                      </v-card>
                    </v-menu>
                  </td>
                  <td>
                    <v-btn class="overline" text small icon v-if="props.item.photoBlobUri">
                      <v-icon>mdi-image</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
              <template v-slot:no-data>
                <v-layout row align-center>
                  <v-flex>No defects have been entered for this Garage</v-flex>
                </v-layout>
              </template>
            </v-data-table>
          </v-expand-transition>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-card class="mx-4">
          <v-card-title class="clickable" @click="openExpanded = !openExpanded">
            Open Notices
            <v-spacer></v-spacer>
            <v-icon v-if="openExpanded">arrow_drop_up</v-icon>
            <v-icon v-else>arrow_drop_down</v-icon>
          </v-card-title>
          <v-expand-transition>
            <v-data-table
              v-show="openExpanded"
              :items="openDefectNotices"
              :headers="openHeaders"
              @update:page="refreshOpenNotices"
              @update:sort-by="refreshOpenNotices"
              @update:sort-desc="refreshOpenNotices"
              @update:items-per-page="refreshOpenNotices"
              :options.sync="openOptions"
              :loading="$wait.is(['loading open defects notices'])"
              :footer-props.sync="openFooterProps"
              :server-items-length="totalOpenNotices"
            >
              <v-progress-linear v-slot:progress color="blue" indeterminate></v-progress-linear>
              <template v-slot:item="props">
                <tr @click="openEdit(props.item)" :class="props.item.hasRemainingPrecompletedToFill ? 'secondary' : ''">
                  <td>{{ moment(props.item.receivedDate) | formatDate }}</td>
                  <td>{{ props.item.ednNumber }}</td>
                  <td>{{ props.item.incidentNumber }}</td>
                  <td class="shrink">{{ props.item.unitNumber }}</td>
                  <td>{{ props.item.unitName }}</td>
                  <td>{{ props.item.receivedTotal }}</td>
                  <td>{{ props.item.scheduledDefectsOutstanding }}</td>
                  <td class="shrink text-center">
                    <v-icon class="green--text" v-if="props.item.isVehicleOffRoad">done </v-icon>
                  </td>
                  <td class="shrink text-center">
                    <v-menu v-if="props.item.hivisStatus && props.item.hivisStatus.status !== 'NotApplicable'">
                      <template v-slot:activator="{ on }">
                        <v-btn height="24" icon text small v-on="on" @click="handleClick">
                          <v-icon :color="getHivisColor(props.item.hivisStatus)">{{ getHivisIcon(props.item.hivisStatus) }}</v-icon>
                        </v-btn>
                      </template>
                      <v-card class="pb-3">
                        <v-card-title class="hivis-headline">
                          <template v-if="props.item.hivisStatus.status === 'HistoricallyAcknowledged' || props.item.hivisStatus.status === 'HistoricallyNotAcknowledged'">
                            HIVIS History ({{ props.item.hivisStatus.historicalDetails.reduce((sum, d) => sum + d.assignees.length, 0) }} Previous)
                          </template>
                          <template v-else>
                            HIVIS Status ({{ props.item.hivisStatus.totalConfirmed }}/{{ props.item.hivisStatus.totalRequired }})
                          </template>
                        </v-card-title>
                        <v-card-text class="hivis-detail" v-for="detail in props.item.hivisStatus.details" :key="detail.lineNumber">
                          <div class="font-weight-bold">Defect {{ detail.lineNumber }}:</div>
                          <div v-for="(assignee, idx) in detail.assignees" :key="idx" class="ml-3">
                            <v-icon small :color="assignee.acknowledged ? 'green' : 'red'">{{ assignee.acknowledged ? 'mdi-check' : 'mdi-close' }}</v-icon>
                            {{ assignee.displayName }}
                            <span v-if="assignee.acknowledged" class="grey--text text--darken-1"> - {{ moment(assignee.acknowledgedAt) | formatDateTime }}</span>
                            <span v-else class="grey--text text--darken-1"> - Not acknowledged</span>
                          </div>
                        </v-card-text>
                        <template v-if="props.item.hivisStatus.historicalDetails && props.item.hivisStatus.historicalDetails.length > 0">
                          <v-divider v-if="props.item.hivisStatus.details.length > 0" class="mt-3 mb-2"></v-divider>
                          <div class="px-4 pt-2 pb-1 font-weight-medium grey--text text--darken-2">
                            <v-icon small color="grey darken-2" class="mr-1">mdi-history</v-icon>
                            Previous Acknowledgements
                          </div>
                          <v-card-text class="hivis-detail pt-1" v-for="detail in props.item.hivisStatus.historicalDetails" :key="'hist-' + detail.lineNumber">
                            <div class="font-weight-bold">Defect {{ detail.lineNumber }}:</div>
                            <div v-for="(assignee, idx) in detail.assignees" :key="idx" class="ml-3">
                              <v-icon small :color="assignee.acknowledged ? 'green' : 'red'">{{ assignee.acknowledged ? 'mdi-check' : 'mdi-close' }}</v-icon>
                              <span class="grey--text text--darken-1">
                                {{ assignee.displayName }}
                                <template v-if="assignee.acknowledged"> - {{ moment(assignee.acknowledgedAt) | formatDateTime }}</template>
                                <template v-else-if="assignee.removedAt"> - {{ moment(assignee.removedAt) | formatDateTime }}</template>
                                <template v-else> - Not acknowledged</template>
                              </span>
                            </div>
                          </v-card-text>
                        </template>
                      </v-card>
                    </v-menu>
                  </td>
                  <td class="shrink text-center">
                    <v-icon class="green--text" v-if="props.item.escalations > 0">done </v-icon>
                  </td>
                  <td class="shrink actions">
                    <v-btn text small icon @click="openMove($event, props.item)" v-if="userIsFleetOrWorkshopManager && props.item.garageLocationId != props.item.unitGarageId" title="Move this EDN to home garage">
                      <v-icon>send</v-icon>
                    </v-btn>
                    <v-btn class="overline" text small icon v-if="props.item.defects.some(d => d.photoBlobUri)">
                      <v-icon>mdi-image</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
              <template v-slot:no-data>
                <v-layout row align-center>
                  <v-flex>No EDN's have been entered for this Garage</v-flex>
                </v-layout>
              </template>
            </v-data-table>
          </v-expand-transition>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-card class="mx-4">
          <v-card-title class="clickable" @click="completedExpanded = !completedExpanded">
            Completed Notices
            <v-spacer></v-spacer>
            <v-icon v-if="completedExpanded">arrow_drop_up</v-icon>
            <v-icon v-else>arrow_drop_down</v-icon>
          </v-card-title>
          <v-expand-transition>
            <v-data-table
              v-show="completedExpanded"
              :items="completedDefectNotices"
              :headers="completedHeaders"
              :loading="$wait.is(['loading completed defects notices'])"
              :footer-props.sync="completedFooterProps"
              :server-items-length="totalCompletedNotices"
              @update:page="refreshCompletedNotices"
              @update:sort-by="refreshCompletedNotices"
              @update:sort-desc="refreshCompletedNotices"
              @update:items-per-page="refreshCompletedNotices"
              :options.sync="completedOptions"
            >
              <v-progress-linear v-slot:progress color="blue" indeterminate></v-progress-linear>
              <template v-slot:item="props">
                <tr @click="openEdit(props.item)" :class="props.item.hasRemainingPrecompletedToFill ? 'secondary' : ''">
                  <td>{{ moment(props.item.receivedDate) | formatDate }}</td>
                  <td>{{ props.item.ednNumber }}</td>
                  <td>{{ props.item.incidentNumber }}</td>
                  <td class="shrink">{{ props.item.unitNumber }}</td>
                  <td>{{ props.item.unitName }}</td>
                  <td>{{ props.item.receivedTotal }}</td>
                  <td class="shrink text-center">
                    <v-icon class="green--text" v-if="props.item.isVehicleOffRoad">done </v-icon>
                  </td>
                  <td class="shrink text-center">
                    <v-menu v-if="props.item.hivisStatus && props.item.hivisStatus.status !== 'NotApplicable'">
                      <template v-slot:activator="{ on }">
                        <v-btn height="24" icon text small v-on="on" @click="handleClick">
                          <v-icon :color="getHivisColor(props.item.hivisStatus)">{{ getHivisIcon(props.item.hivisStatus) }}</v-icon>
                        </v-btn>
                      </template>
                      <v-card class="pb-3">
                        <v-card-title class="hivis-headline">
                          <template v-if="props.item.hivisStatus.status === 'HistoricallyAcknowledged' || props.item.hivisStatus.status === 'HistoricallyNotAcknowledged'">
                            HIVIS History ({{ props.item.hivisStatus.historicalDetails.reduce((sum, d) => sum + d.assignees.length, 0) }} Previous)
                          </template>
                          <template v-else>
                            HIVIS Status ({{ props.item.hivisStatus.totalConfirmed }}/{{ props.item.hivisStatus.totalRequired }})
                          </template>
                        </v-card-title>
                        <v-card-text class="hivis-detail" v-for="detail in props.item.hivisStatus.details" :key="detail.lineNumber">
                          <div class="font-weight-bold">Defect {{ detail.lineNumber }}:</div>
                          <div v-for="(assignee, idx) in detail.assignees" :key="idx" class="ml-3">
                            <v-icon small :color="assignee.acknowledged ? 'green' : 'red'">{{ assignee.acknowledged ? 'mdi-check' : 'mdi-close' }}</v-icon>
                            {{ assignee.displayName }}
                            <span v-if="assignee.acknowledged" class="grey--text text--darken-1"> - {{ moment(assignee.acknowledgedAt) | formatDateTime }}</span>
                            <span v-else class="grey--text text--darken-1"> - Not acknowledged</span>
                          </div>
                        </v-card-text>
                        <template v-if="props.item.hivisStatus.historicalDetails && props.item.hivisStatus.historicalDetails.length > 0">
                          <v-divider v-if="props.item.hivisStatus.details.length > 0" class="mt-3 mb-2"></v-divider>
                          <div class="px-4 pt-2 pb-1 font-weight-medium grey--text text--darken-2">
                            <v-icon small color="grey darken-2" class="mr-1">mdi-history</v-icon>
                            Previous Acknowledgements
                          </div>
                          <v-card-text class="hivis-detail pt-1" v-for="detail in props.item.hivisStatus.historicalDetails" :key="'hist-' + detail.lineNumber">
                            <div class="font-weight-bold">Defect {{ detail.lineNumber }}:</div>
                            <div v-for="(assignee, idx) in detail.assignees" :key="idx" class="ml-3">
                              <v-icon small :color="assignee.acknowledged ? 'green' : 'red'">{{ assignee.acknowledged ? 'mdi-check' : 'mdi-close' }}</v-icon>
                              <span class="grey--text text--darken-1">
                                {{ assignee.displayName }}
                                <template v-if="assignee.acknowledged"> - {{ moment(assignee.acknowledgedAt) | formatDateTime }}</template>
                                <template v-else-if="assignee.removedAt"> - {{ moment(assignee.removedAt) | formatDateTime }}</template>
                                <template v-else> - Not acknowledged</template>
                              </span>
                            </div>
                          </v-card-text>
                        </template>
                      </v-card>
                    </v-menu>
                  </td>
                  <td class="shrink text-center">
                    <v-icon class="green--text" v-if="props.item.escalations > 0">done </v-icon>
                  </td>
                  <td class="shrink actions">
                    <v-btn text small icon v-if="userIsFleetOrWorkshopManager && props.item.garageLocationId != props.item.unitGarageId" @click="openMove($event, props.item)" title="Move this EDN to home garage">
                      <v-icon>send</v-icon>
                    </v-btn>
                    <v-btn class="overline" text small icon v-if="props.item.defects.some(d => d.photoBlobUri)">
                      <v-icon>mdi-image</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
              <template v-slot:no-data>
                <v-layout row align-center>
                  <v-flex>No EDN's have been entered for this Garage</v-flex>
                </v-layout>
              </template>
            </v-data-table>
          </v-expand-transition>
        </v-card>
      </v-col>
    </v-row>
    <v-dialog persistent v-model="dialogs.showDefectNoticeEntry" max-width="500px">
      <entry-form :value="model" ref="addform" @receive="submitReceive($event.id, $event.model, $event.actionNow)">
        <template v-slot:header>
          <v-spacer></v-spacer>
        </template>
        <template v-slot:actions>
          <v-btn @click="closeDefectNoticeEntry">Cancel</v-btn>
        </template>
      </entry-form>
    </v-dialog>
    <v-dialog persistent v-model="dialogs.showDefectActionEntry" :max-width="showLog ? '90vw' : '45vw'">
      <v-row no-gutters>
        <v-col :cols="showLog ? 8 : 12">
          <action-form
            :value="model"
            ref="actionform"
            @action="submitAction($event.id, $event.model, $event.newReceivedTotal)"
            @delete-edn="deleteEdn($event.id, $event.source)"
            @refresh-page="handleRefreshPage"
            @close-modal="closeDefectActionEntry"
          >
            <template v-slot:header>
              <v-spacer></v-spacer>
              <v-btn @click="toggleLog" class="mr-2" color="accent" v-show="userIsFleetManager">
                Logs
                <v-icon small v-if="showLog">chevron_left</v-icon>
                <v-icon small v-else>chevron_right</v-icon>
              </v-btn>
            </template>
            <template v-slot:actions>
              <v-btn @click="closeDefectActionEntry">Cancel</v-btn>
            </template>
          </action-form>
        </v-col>
        <v-col v-if="showLog && userIsFleetManager" cols="4">
          <defect-notice-audit-log :defect-notice-id="model.id"></defect-notice-audit-log>
        </v-col>
      </v-row>
    </v-dialog>
    <v-dialog persistent v-model="dialogs.showScheduleOption" max-width="500px">
      <selection-form ref="selectionform" :model="dialogs.schedulingModel" @schedule-now="scheduleNow" @schedule-later="scheduleLater"></selection-form>
    </v-dialog>
    <!-- Open Notices -->
    <v-dialog persistent v-model="dialogs.showDefectScheduleEntry" :max-width="showCalendar || showLog || showChildLog ? '90vw' : '60vw'">
      <v-row no-gutters>
        <v-col :cols="showCalendar ? 9 : showLog || showChildLog ? 8 : 12">
          <schedule-form :value="model" ref="scheduleform" @delete-edn="deleteEdn($event.id, $event.source)" @refresh-page="handleRefreshPage" @close-modal="closeScheduleForm">
            <template v-slot:header>
              <v-spacer></v-spacer>
              <v-btn v-if="model.sourceSystem === 'OnRoad'" @click="exportPdf(model)" color="accent" class="mr-2">PDF Export</v-btn>
              <v-btn @click="toggleLog" class="mr-2" color="accent" v-show="!showCalendar && !showChildLog && userIsFleetManager">
                Logs
                <v-icon small v-if="showLog">chevron_left</v-icon>
                <v-icon small v-else>chevron_right</v-icon>
              </v-btn>
              <v-btn @click="toggleCalendar" color="accent" v-show="!showLog && !showChildLog">
                Calendar
                <v-icon small v-if="showCalendar">chevron_left</v-icon>
                <v-icon small v-else>chevron_right</v-icon>
              </v-btn>
            </template>
            <template v-slot:actions>
              <v-btn @click="closeScheduleForm">Cancel</v-btn>
            </template>
          </schedule-form>
        </v-col>
        <v-col v-if="showLog && userIsFleetManager" cols="4">
          <defect-notice-audit-log :defect-notice-id="model.id"></defect-notice-audit-log>
        </v-col>
        <v-col v-if="showChildLog" cols="4">
          <defect-audit-log :defect-id="childLogDefectId" :defect-number="childLogDefectNumber" :work-sessions="childLogWorkSessions" :assignees="childLogAssignees" :historical-accepted-jobs="childLogHistoricalAcceptedJobs"></defect-audit-log>
        </v-col>
        <v-col v-show="showCalendar" cols="3">
          <calendar-preview ref="calendar"></calendar-preview>
        </v-col>
      </v-row>
    </v-dialog>
    <v-dialog persistent v-model="dialogs.showMove" max-width="500px">
      <move-form :value="model" ref="moveform" @move="submitMove($event.id, $event.newGarageId)">
        <template v-slot:header>
          <v-spacer></v-spacer>
        </template>
        <template v-slot:actions>
          <v-btn @click="closeMoveForm">Cancel</v-btn>
        </template>
      </move-form>
    </v-dialog>
    <v-dialog persistent v-model="dialogs.showReceiveEdn" :max-width="showLog || showChildLog ? '90vw' : '60vw'">
      <v-row no-gutters>
        <v-col :cols="showLog || showChildLog ? 8 : 12">
          <receive-form
            :value="model"
            ref="receiveform"
            @receive="submitOnRoad($event.id, $event.model, $event.actionNow)"
            @delete-edn="deleteEdn($event.id, $event.source)"
            @refresh-page="handleRefreshPage"
            @close-modal="closeReceiveForm"
          >
            <template v-slot:header>
              <v-spacer></v-spacer>
              <v-btn @click="toggleLog" class="mr-2" color="accent" v-show="!showChildLog && userIsFleetManager">
                Logs
                <v-icon small v-if="showLog">chevron_left</v-icon>
                <v-icon small v-else>chevron_right</v-icon>
              </v-btn>
            </template>
            <template v-slot:actions>
              <v-btn @click="closeReceiveForm">Cancel</v-btn>
            </template>
          </receive-form>
        </v-col>
        <v-col v-if="showLog && userIsFleetManager" cols="4">
          <defect-notice-audit-log :defect-notice-id="model.id"></defect-notice-audit-log>
        </v-col>
        <v-col v-if="showChildLog" cols="4">
          <defect-audit-log :defect-id="childLogDefectId" :defect-number="childLogDefectNumber" :work-sessions="childLogWorkSessions" :assignees="childLogAssignees" :historical-accepted-jobs="childLogHistoricalAcceptedJobs"></defect-audit-log>
        </v-col>
      </v-row>
    </v-dialog>
    <v-dialog persistent v-model="dialogs.showRejectDefect" max-width="500px">
      <reject-defect-form :defect="model" ref="rejectdefectform" @reject="submitRejectedDefect($event.id, $event.model)" @refresh-page="handleRefreshPage" @close-modal="closeRejectDefectForm">
        <template v-slot:header>
          <v-btn @click="closeRejectDefectForm" icon text>
            <v-icon>close</v-icon>
          </v-btn>
        </template>
        <template v-slot:actions>
          <v-btn @click="closeRejectDefectForm">{{ dialogs.editingRejectDefect ? 'Cancel' : 'Close' }} </v-btn>
        </template>
      </reject-defect-form>
    </v-dialog>
    <v-dialog persistent v-model="dialogs.showScheduleDefect" :max-width="showCalendar || showLog || showChildLog ? '90vw' : '60vw'">
      <v-row no-gutters>
        <v-col :cols="showCalendar ? 9 : showLog || showChildLog ? 8 : 12">
          <schedule-defect-form :defect="model" ref="scheduledefectform" @schedule="submitScheduledDefect($event.id, $event.model)" @refresh-page="handleRefreshPage" @close-modal="closeScheduleDefectForm">
            <template v-slot:header>
              <v-btn @click="toggleLog" color="accent" class="mr-2" v-show="!showCalendar && !showChildLog && userIsFleetManager">
                Logs
                <v-icon small v-if="showLog">chevron_left</v-icon>
                <v-icon small v-else>chevron_right</v-icon>
              </v-btn>
              <v-btn @click="showCalendar = !showCalendar" color="accent" v-show="!showLog">
                Calendar
                <v-icon small v-if="showCalendar">chevron_left</v-icon>
                <v-icon small v-else>chevron_right</v-icon>
              </v-btn>
            </template>
            <template v-slot:actions>
              <v-spacer></v-spacer>
              <v-btn @click="closeScheduleDefectForm">Cancel</v-btn>
              <v-btn color="red white--text" @click="openRejectDefect($event, model)">Reject</v-btn>
            </template>
          </schedule-defect-form>
        </v-col>
        <v-col v-if="showLog && userIsFleetManager" cols="4">
          <defect-notice-audit-log :defect-notice-id="model.id"></defect-notice-audit-log>
        </v-col>
        <v-col v-if="showChildLog" cols="4">
          <defect-audit-log :defect-id="childLogDefectId" :defect-number="childLogDefectNumber" :work-sessions="childLogWorkSessions" :assignees="childLogAssignees" :historical-accepted-jobs="childLogHistoricalAcceptedJobs"></defect-audit-log>
        </v-col>
        <v-col v-if="showCalendar" cols="3">
          <calendar-preview ref="calendar"></calendar-preview>
        </v-col>
      </v-row>
    </v-dialog>
    <v-dialog persistent v-model="dialogs.showAssignedDefects">
      <defect-assignee-form @close-modal="closeAssignedDefectsForm" :assignments="defectAssignment.assigned"> </defect-assignee-form>
    </v-dialog>
    <complete-defect></complete-defect>
  </v-container>
</template>

<script>
import { Event } from '@/plugins/bus';
import EntryForm from './DefectEntryDialogs/Step1EntryForm.vue';
import ActionForm from './DefectEntryDialogs/Step2ActionForm.vue';
import ScheduleForm from './DefectEntryDialogs/Step4ScheduleForm.vue';
import SelectionForm from './DefectEntryDialogs/Step3ScheduleSelectionForm.vue';
import MoveForm from './DefectEntryDialogs/MoveForm.vue';
import DefectNotices from '@/components/DefectNotices/DefectNoticesClient.vue';
import DefectNoticesFilter from '@/components/DefectNotices/DefectNoticesFilter.vue';
import GarageLocations from '@/components/GarageLocations/GarageLocationsClient.vue';
import CalendarPreview from './DefectEntryDialogs/CalendarPreview.vue';
import CompleteDefect from '@/components/DefectNotices/CompleteDefect.vue';
import RejectDefectForm from '@/components/DefectNotices/RejectDefectForm.vue';
import ScheduleDefectForm from '@/components/DefectNotices/ScheduleDefectForm.vue';
import ReceiveForm from '@/components/DefectNotices/DefectEntryDialogs/Step1ReceiveEntryForm.vue';
import DefectAssigneeForm from '@/components/DefectNotices/DefectAssigneeForm.vue';
import { DateFormat } from '@/plugins/date-filter';
import moment from 'moment';
import router from '@/router';
import DownloadHelper from '@/lib/DownloadHelper';
import DefectAuditLog from '@/components/DefectNotices/DefectAuditLog.vue';
import DefectNoticeAuditLog from '@/components/DefectNotices/DefectNoticeAuditLog.vue';

const ACTION_REQUIRED_DEFECT_STATUSES = ['Submitted', 'Received'];
const CHILD_DEFECT_SOURCE_SYSTEMS = ['OnRoad', 'InShop'];

export default {
  mixins: [DefectNotices],
  components: {
    DefectNoticeAuditLog,
    DefectAuditLog,
    ReceiveForm,
    EntryForm,
    ActionForm,
    SelectionForm,
    ScheduleForm,
    DefectNoticesFilter,
    CalendarPreview,
    MoveForm,
    CompleteDefect,
    RejectDefectForm,
    ScheduleDefectForm,
    DefectAssigneeForm
  },
  GarageLocations,
  data() {
    return {
      filterParams: {
        unitId: null,
        ednNumber: null,
        freeText: null,
        dateField: null,
        startDate: null,
        endDate: null
      },
      dialogs: {
        showDefectNoticeEntry: false,
        showDefectActionEntry: false,
        showDefectScheduleEntry: false,
        showScheduleOption: false,
        schedulingModel: null,
        showMove: false,
        showReceiveEdn: false,
        showRejectDefect: false,
        editingRejectDefect: false,
        showScheduleDefect: false,
        showAssignedDefects: false
      },
      showSearch: false,
      reviewExpanded: false,
      openExpanded: false,
      completedExpanded: false,
      dateFormat: DateFormat.DefaultDisplay,
      model: {},
      garageLocationId: '',
      previousReviewOptions: '',
      previousOpenOptions: '',
      previousCompletedOptions: '',
      unactionedOptions: {
        sortBy: ['receivedDate'],
        sortDescending: false
      },
      unscheduledOptions: {
        sortBy: ['receivedDate'],
        sortDescending: false
      },
      reviewOptions: {
        sortBy: ['receivedDate'],
        sortDesc: [true]
      },
      openOptions: {
        sortBy: ['receivedDate'],
        sortDesc: [true]
      },
      completedOptions: {
        sortBy: ['receivedDate'],
        sortDesc: [true]
      },
      reviewFooterProps: {
        itemsPerPageOptions: [10, 50, 100]
      },
      openFooterProps: {
        itemsPerPageOptions: [10, 50, 100]
      },
      completedFooterProps: {
        itemsPerPageOptions: [10, 50, 100]
      },
      unactionedHeaders: [
        { text: 'Received Date', value: 'receivedDate', sortable: true },
        { text: 'EDN', value: 'ednNumber' },
        { text: 'Incident/Reference #', value: 'incidentNumber' },
        { text: 'System', value: 'SourceSystem', sortable: false },
        { text: 'Unit', value: 'unitNumber' },
        { text: 'Received', value: 'receivedTotal', sortable: false },
        { text: 'Created', value: 'createdOn', sortable: false },
        { text: '', value: '', sortable: false }
      ],
      schedulingHeaders: [
        { text: 'Received Date', value: 'receivedDate' },
        { text: 'EDN', value: 'ednNumber' },
        { text: 'Incident/Reference #', value: 'incidentNumber' },
        { text: 'Unit', value: 'unitNumber' },
        { text: 'Received', value: 'receivedTotal', sortable: false },
        { text: 'Completed on Receipt', value: 'completedOnReceipt', sortable: false },
        { text: '', value: '', sortable: false }
      ],
      reviewHeaders: [
        { text: 'Received Date', value: 'receivedDate', sortable: true },
        { text: 'EDN', value: 'ednNumber' },
        { text: 'Description', value: 'description' },
        { text: 'Status', value: 'status', sortable: false },
        { text: 'Unit', value: 'unitNumber' },
        { text: '', value: 'unitName' },
        { text: 'HIVIS?', value: 'hivisStatus', sortable: false, align: 'center' },
        { text: '', value: 'defectPhoto' }
      ],
      openHeaders: [
        { text: 'Received Date', value: 'receivedDate' },
        { text: 'EDN', value: 'ednNumber' },
        { text: 'Incident/Reference #', value: 'incidentNumber' },
        { text: 'Unit', value: 'unitNumber', sortable: false },
        { text: 'Name', value: 'unitName', sortable: false },
        { text: 'Total', value: 'receivedTotal', sortable: false },
        { text: 'Remaining', value: 'scheduledDefectsOutstanding', sortable: false },
        { text: 'VOR?', value: 'isVehicleOffRoad', sortable: false, align: 'center' },
        { text: 'HIVIS?', value: 'hivisConfirmations', sortable: false, align: 'center' },
        { text: 'Escalations?', value: 'escalations', sortable: false, align: 'center' },
        { text: '', value: '', sortable: false }
      ],
      completedHeaders: [
        { text: 'Received Date', value: 'receivedDate' },
        { text: 'EDN', value: 'ednNumber' },
        { text: 'Incident/Reference #', value: 'incidentNumber' },
        { text: 'Unit', value: 'unitNumber', sortable: false },
        { text: 'Name', value: 'unitName', sortable: false },
        { text: 'Total', value: 'receivedTotal' },
        { text: 'VOR?', value: 'isVehicleOffRoad', sortable: false, align: 'center' },
        { text: 'HIVIS?', value: 'hivisConfirmations', sortable: false, align: 'center' },
        { text: 'Escalations?', value: 'escalations', sortable: false },
        { text: '', value: '', sortable: false }
      ],
      showCalendar: false,
      showLog: false,
      showChildLog: false,
      childLogDefectId: null,
      childLogWorkSessions: null,
      childLogAssignees: null,
      childLogHistoricalAcceptedJobs: null,
      childLogDefectNumber: null,
      now: new Date(),
      times: {},
      timeInterval: null
    };
  },
  async mounted() {
    this.garageLocationId = this.params.garageLocationId;
    await this.getDefectNotices();
  },
  async created() {
    this.$bus.$on(Event.DefectSchedulesUpdated, this.handleDefectSchedulesUpdate);
    this.$bus.$on(Event.DefectCompleted, this.handleDefectCompleted);
    this.$bus.$on(Event.DefectRejected, this.handleDefectRejected);
    this.$bus.$on(Event.GarageSelected, this.onGarageSelected);
    this.$bus.$on(Event.ChildLogVisibilityChanged, this.onChildLogVisibilityChanged);
    this.$bus.$on(Event.CloseAllLogs, this.onCloseAllLogs);

    this.timeInterval = setInterval(() => (this.now = new Date()), 10);
  },
  destroyed() {
    this.$bus.$off(Event.DefectSchedulesUpdated, this.handleDefectSchedulesUpdate);
    this.$bus.$off(Event.GarageSelected, this.onGarageSelected);
    this.$bus.$off(Event.DefectCompleted, this.handleDefectCompleted);
    this.$bus.$off(Event.ChildLogVisibilityChanged, this.onChildLogVisibilityChanged);
    this.$bus.$off(Event.DefectRejected, this.handleDefectRejected);
    this.$bus.$off(Event.CloseAllLogs, this.onCloseAllLogs);
    if (this.timeInterval) {
      clearInterval(this.timeInterval);
    }
    this.showLog = false;
    this.showChildLog = false;
  },
  watch: {
    filterParams: {
      async handler(filter) {
        await this.getDefectNotices();
      },
      deep: true
    },
    defectsToAction: {
      async handler() {
        this.now = new Date(); //force update of times
      }
    },
    now: {
      async handler() {
        if (this.defectsToAction && this.defectsToAction.length) {
          const times = {};
          for (let defect of this.defectsToAction) {
            times[defect.id] = this.timespan(defect.startedAction);
          }
          this.times = times;
        }
      }
    }
  },
  methods: {
    countActionRequiredDefects(defectNotice) {
      if (!defectNotice) {
        return 0;
      }

      const defects = defectNotice.defects || [];
      const countByChildDefects =
        CHILD_DEFECT_SOURCE_SYSTEMS.includes(defectNotice.sourceSystem) &&
        defects.length > 0;

      if (countByChildDefects) {
        return defects.filter(defect => ACTION_REQUIRED_DEFECT_STATUSES.includes(defect.status)).length;
      }

      return Number(defectNotice.receivedTotal) || 0;
    },
    async exportPdf(defectNotice) {
      try {
        this.$wait.start('exporting defects pdf');
        const response = await this.$api.get(`defect-notices/${defectNotice.id}/generate/pdf`, {
          responseType: 'blob'
        });
        let filename = `edn_${defectNotice.ednNumber}.pdf`;
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }
        const blob = new Blob([response.data], { type: 'application/pdf' });
        DownloadHelper.download(filename, blob);
      } catch (err) {
        this.$toast.error('There was an error generating the PDF report');
      } finally {
        this.$wait.end('exporting defects pdf');
      }
    },
    onCloseAllLogs() {
      this.$bus.$emit(Event.ParentLogVisibilityChanged, { visible: false });
      this.showLog = false;
      this.showChildLog = false;
      this.showCalendar = false;
    },
    toggleLog() {
      this.$bus.$emit(Event.ParentLogVisibilityChanged, { visible: !this.showLog });
      this.showLog = !this.showLog;
      this.showCalendar = false;
    },
    toggleCalendar() {
      this.$bus.$emit(Event.ParentLogVisibilityChanged, { visible: !this.showCalendar });
      this.showCalendar = !this.showCalendar;
      this.showChildLog = false;
      this.showLog = false;
    },
    closeDefectNoticeEntry() {
      this.dialogs.showDefectNoticeEntry = false;
      this.$bus.$emit(Event.CloseAllLogs);
    },
    closeDefectActionEntry() {
      this.dialogs.showDefectActionEntry = false;
      this.$bus.$emit(Event.CloseAllLogs);
    },
    closeScheduleForm() {
      this.dialogs.showDefectScheduleEntry = false;
      this.$bus.$emit(Event.CloseAllLogs);
    },
    closeMoveForm() {
      this.dialogs.showMove = false;
      this.$bus.$emit(Event.CloseAllLogs);
    },
    closeReceiveForm() {
      this.dialogs.showReceiveEdn = false;
      this.$bus.$emit(Event.CloseAllLogs);
    },
    closeRejectDefectForm() {
      this.dialogs.showRejectDefect = false;
      this.$bus.$emit(Event.CloseAllLogs);
    },
    closeScheduleDefectForm() {
      this.dialogs.showScheduleDefect = false;
      this.$bus.$emit(Event.CloseAllLogs);
    },
    closeAssignedDefectsForm() {
      this.dialogs.showAssignedDefects = false;
      this.$bus.$emit(Event.CloseAllLogs);
    },
    async getDefectNotices() {
      const pendingAwaiter = this.getPendingDefectNotices();
      const openAwaiter = this.getOpenDefectNotices();
      const completedAwaiter = this.getCompletedDefectNotices();
      const availableAssigneesAwaiter = this.getAvailableAssignees();
      const reviewAwaiter = this.getReviewDefects();
      const assignmentAwaiter = this.getAssignmentStatus();

      await pendingAwaiter;
      await openAwaiter;
      await completedAwaiter;
      await availableAssigneesAwaiter;
      await reviewAwaiter;
      await assignmentAwaiter;
    },
    async refreshReviewDefects() {
      if (this.previousReviewOptions !== this.snapshot(this.reviewOptions)) {
        this.previousReviewOptions = this.snapshot(this.reviewOptions);
        await this.getReviewDefects();
      }
    },
    async refreshOpenNotices() {
      if (this.previousOpenOptions !== this.snapshot(this.openOptions)) {
        this.previousOpenOptions = this.snapshot(this.openOptions);
        await this.getOpenDefectNotices();
      }
    },
    async refreshCompletedNotices() {
      if (this.previousCompletedOptions !== this.snapshot(this.completedOptions)) {
        this.previousCompletedOptions = this.snapshot(this.completedOptions);
        await this.getCompletedDefectNotices();
      }
    },
    async onGarageSelected() {
      if (this.garageLocationId !== this.params.garageLocationId) {
        this.garageLocationId = this.params.garageLocationId;
        await this.getDefectNotices();
      }
    },
    searchInitiated() {
      setTimeout(() => {
        this.openExpanded = true;
        this.completedExpanded = true;
      }, 100);
    },
    async handleDefectCompleted(event) {
      const currentId = this.model.id;
      await this.getDefectNotices();
      if (event.initiatedFrom !== 'for-review') {
        this.openEdit(await this.getDefectNotice(currentId));
      }
    },
    async handleDefectRejected() {
      await this.getDefectNotices();
    },
    openReceive() {
      this.model = {};
      this.dialogs.showDefectNoticeEntry = true;

      if (this.$refs.addform) {
        this.$refs.addform.init();
      } else {
        setTimeout(() => this.$refs.addform.init(), 10);
      }
    },
    async checkForStaleData(defectNotice) {
      try {
        this.$wait.start('checking data freshness');
        const currentDefectNotice = await this.getDefectNotice(defectNotice.id);

        // Check if the data is stale by comparing key fields
        const isStale = this.isDefectNoticeStale(defectNotice, currentDefectNotice);

        return {
          ...defectNotice,
          isStale
        };
      } catch (error) {
        // If we can't fetch current data, assume it's not stale
        return defectNotice;
      } finally {
        this.$wait.end('checking data freshness');
      }
    },

    async openAction(defectNotice) {
      // Check for stale data by fetching the current defect notice from the database
      this.model = await this.checkForStaleData(defectNotice);

      if (defectNotice.sourceSystem === 'OnRoad' || defectNotice.source === 'InShop') {
        this.dialogs.showReceiveEdn = true;
        await this.initLogs();
        setTimeout(() => this.$refs.receiveform.init(), 10);
      }

      if (defectNotice.sourceSystem === 'GateKeeper') {
        this.dialogs.showDefectActionEntry = true;
        await this.initLogs();
        setTimeout(() => this.$refs.actionform.init(), 10);
      }
    },
    openScheduleOptions(model) {
      this.dialogs.showScheduleOption = true;
      this.dialogs.schedulingModel = model;
    },
    async openSchedule(defectNotice) {
      // Check for stale data by fetching the current defect notice from the database
      this.model = await this.checkForStaleData(defectNotice);

      this.dialogs.showDefectScheduleEntry = true;
      await this.initCalendar();
      await this.initLogs();

      setTimeout(() => this.$refs.scheduleform.init(), 10);
    },
    async openEdit(defectNotice) {
      // Check for stale data by fetching the current defect notice from the database
      this.model = await this.checkForStaleData(defectNotice);

      this.dialogs.showDefectScheduleEntry = true;
      await this.initCalendar();
      await this.initLogs();

      setTimeout(() => this.$refs.scheduleform.init(true), 10);
    },
    openMove($event, defectNotice) {
      $event.stopPropagation();
      this.model = defectNotice;
      this.dialogs.showMove = true;

      setTimeout(() => this.$refs.moveform.init(), 10);
    },
    openRejectDefect($event, defect) {
      $event.stopPropagation();
      this.model = defect;
      this.dialogs.editingRejectDefect = true;
      setTimeout(() => this.$refs.rejectdefectform.init(this.dialogs.editingRejectDefect), 10);
      this.closeScheduleDefectForm();
      this.dialogs.showRejectDefect = true;
    },
    async checkForStaleDefect(defect) {
      try {
        this.$wait.start('checking data freshness');
        const currentDefect = await this.getDefect(defect.id);
        const isStale = this.isDefectStale(defect, currentDefect);
        return {
          ...defect,
          isStale
        };
      } catch (error) {
        return defect;
      } finally {
        this.$wait.end('checking data freshness');
      }
    },

    async openAcceptDefect(defect) {
      if (defect.status === 'CompletedPendingReview') {
        router.push({ query: { defectId: defect.id, initiatedFrom: 'for-review' } });
      }

      // Check for stale data by fetching the current defect from the database
      this.model = await this.checkForStaleDefect(defect);

      if (defect.status === 'ScheduledPendingReview') {
        await this.initCalendar();
        setTimeout(() => this.$refs.scheduledefectform.init(), 10);
        this.dialogs.showScheduleDefect = true;
      }

      if (defect.status === 'PendingResubmission') {
        this.dialogs.editingRejectDefect = false;
        setTimeout(() => this.$refs.rejectdefectform.init(this.dialogs.editingRejectDefect), 10);
        this.dialogs.showRejectDefect = true;
      }
    },
    async onChildLogVisibilityChanged(event) {
      // hide my log buttons if a child is displaying its logs
      this.childLogDefectId = event.defectId;
      this.childLogWorkSessions = event.workSessions;
      this.childLogAssignees = event.assignees;
      this.childLogHistoricalAcceptedJobs = event.historicalAcceptedJobs;
      this.childLogDefectNumber = event.defectNumber;
      this.showChildLog = event.visible;
    },
    async deleteEdn(defectNoticeId, sourceDialog) {
      if (sourceDialog == 'schedule') {
        this.closeScheduleForm();
      } else if (sourceDialog == 'action') {
        this.closeDefectActionEntry();
      } else if (sourceDialog == 'onroad-receive') {
        this.closeReceiveForm();
      }

      try {
        this.$wait.start('deleting defect notice');
        await this.$api.delete(`defect-notices/${defectNoticeId}`);
        this.$toast.success('Defect Notice deleted');
        await this.getDefectNotices();
      } catch (err) {
        this.$toast.error('Could not delete Defect Notice');
        this.$toast.error(err);
      } finally {
        this.$wait.end('deleting defect notice');
      }
    },
    async scheduleLater() {
      this.dialogs.showScheduleOption = false;
      await this.getDefectNotices();
    },
    async scheduleNow(defectNotice) {
      // fetch a new copy of the defect notice to pass the stale data check
      // (the model we get from the previous screen is missing some values that break the check)
      const updatedDefectNotice = await this.getDefectNotice(defectNotice.id);
      this.dialogs.showScheduleOption = false;
      this.dialogs.schedulingModel = null;
      await this.openSchedule(updatedDefectNotice);
    },
    async submitReceive(id, model, actionNow) {
      const result = await this.addDefectNotice(id, model);
      if (result && result.status >= 200 && result.status <= 299) {
        this.closeDefectNoticeEntry();
        await this.getDefectNotices();

        if (actionNow) {
          this.openAction({
            ...model,
            precompletedDefects: [],
            scheduled: [],
            id
          });
        }
      }
    },
    async submitOnRoad(id, model) {
      const result = await this.receiveDefectNotice(id, model);
      if (result && result.status >= 200 && result.status <= 299) {
        this.closeReceiveForm();
        await this.getDefectNotices();
      }
    },
    async submitRejectedDefect(id, model) {
      const result = await this.rejectDefect(id, model);
      if (result && result.status >= 200 && result.status <= 299) {
        this.dialogs.editingRejectDefect = false;
        this.closeRejectDefectForm();
      }
    },
    async submitScheduledDefect(id, model) {
      const result = await this.scheduleDefect(id, model);
      if (result && result.status >= 200 && result.status <= 299) {
        this.closeScheduleDefectForm();
        this.$refs.scheduledefectform.reset();
        await this.getDefectNotices();
      }
    },
    async submitAction(id, model, newReceivedTotal) {
      const result = await this.actionDefectNotice(id, model, newReceivedTotal);
      if (result && result.status >= 200 && result.status <= 299) {
        this.closeDefectActionEntry();
        model.receivedTotal = newReceivedTotal;
        await this.getDefectNotices();
        if (model.completedOnReceipt < model.receivedTotal) {
          await this.openScheduleOptions(model);
        }
      }
    },
    async submitUpdate(id, newGarageId) {
      const result = await this.addDefectNotice(id, newGarageId);
      if (result && result.status >= 200 && result.status <= 299) {
        this.closeDefectNoticeEntry();
        await this.getDefectNotices();
      }
    },
    async submitMove(id, newGarageId) {
      const result = await this.moveDefectNotice(id, newGarageId);
      if (result && result.status >= 200 && result.status <= 299) {
        this.closeMoveForm();
        await this.getDefectNotices();
      }
    },
    async submitStart($event, id) {
      $event.stopPropagation();
      const result = await this.startDefectNotice(id);
      if (result && result.status >= 200 && result.status <= 299) {
        await this.getDefectNotices();
      }
    },
    async handleDefectSchedulesUpdate(id, scheduledDefects) {
      this.closeScheduleForm();
      await this.getDefectNotices();
    },
    async initCalendar() {
      this.showCalendar = false;
      setTimeout(() => this.$refs.calendar.init(), 1);
    },
    async initLogs() {
      this.showLog = false;
    },
    showSearchBar() {
      this.showSearch = true;
    },
    hideSearchBar() {
      this.showSearch = false;
      this.$emit('closesearch');
    },
    timespan(since) {
      const duration = moment.duration(moment(new Date()).diff(moment(since)));

      const pad = x => `${x < 10 ? '0' : ''}${x}`;
      const hours = Math.floor(duration.asHours());
      const minutes = duration.minutes();

      return `${pad(hours)}:${pad(minutes)}`;
    },
    async refresh() {
      await this.getDefectNotices();
    },
    isDefectNoticeStale(displayedData, currentData) {
      if (!currentData) {
        return false;
      }

      // Compare key fields that would indicate the data has changed
      const keyFields = ['receivedTotal', 'completedOnReceipt', 'receivedDate', 'incidentNumber', 'sourceId', 'scheduledCount'];

      // Check if any key fields have changed
      for (const field of keyFields) {
        if (displayedData[field] !== currentData[field]) {
          return true;
        }
      }

      // Check if any of the defect arrays have changed
      const defectArrays = [
        { displayed: displayedData.defects, current: currentData.defects },
        { displayed: displayedData.scheduled, current: currentData.scheduled },
        { displayed: displayedData.precompletedDefects, current: currentData.precompletedDefects },
        { displayed: displayedData.completedDefects, current: currentData.completedDefects }
      ];

      for (const { displayed, current } of defectArrays) {
        if (this.areDefectsStale(displayed, current)) {
          return true;
        }
      }

      return false;
    },
    areDefectsStale(displayedDefects, currentDefects) {
      if (!displayedDefects || !currentDefects) {
        return false;
      }

      if (displayedDefects.length !== currentDefects.length) {
        return true;
      }

      // Check if any defect statuses have changed
      for (let i = 0; i < displayedDefects.length; i++) {
        if (this.isDefectStale(displayedDefects[i], currentDefects[i])) {
          return true;
        }
      }

      return false;
    },
    isDefectStale(displayedDefect, currentDefect) {
      if (!currentDefect || displayedDefect.status !== currentDefect.status) {
        return true;
      }
    },
    async handleRefreshPage() {
      await this.refresh();
    },
    openAssignedDefects() {
      this.dialogs.showAssignedDefects = true;
    },
    handleClick(e) {
      e.stopPropagation();
      e.preventDefault();
    },
    noticeAssignedColor(defects) {
      if (!defects || defects.length === 0) {
        return 'grey';
      }

      const defectsWithAssignees = defects.filter(defect => defect.assignees && defect.assignees.length > 0);

      if (defectsWithAssignees.length === 0) {
        return 'grey'; // No defects have assignees
      } else if (defectsWithAssignees.length === defects.length) {
        return 'green'; // All defects have assignees
      } else {
        return 'orange'; // Some defects have assignees
      }
    },
    getHivisColor(hivisStatus) {
      if (!hivisStatus) return 'grey';
      switch (hivisStatus.status) {
        case 'AllAcknowledged':
          return 'green';
        case 'PartiallyAcknowledged':
          return 'orange';
        case 'NoneAcknowledged':
          return 'red';
        case 'HistoricallyAcknowledged':
          return 'green';
        case 'HistoricallyNotAcknowledged':
          return 'red';
        case 'NotApplicable':
        default:
          return 'grey';
      }
    },
    getHivisIcon(hivisStatus) {
      if (!hivisStatus) return 'mdi-minus';
      switch (hivisStatus.status) {
        case 'AllAcknowledged':
          return 'mdi-check';
        case 'PartiallyAcknowledged':
          return 'mdi-alert';
        case 'NoneAcknowledged':
          return 'mdi-close';
        case 'HistoricallyAcknowledged':
          return 'mdi-history';
        case 'HistoricallyNotAcknowledged':
          return 'mdi-history';
        case 'NotApplicable':
        default:
          return 'mdi-minus';
      }
    }
  },
  computed: {
    countDefectsToAction() {
      const count = (this.defectsToAction || []).reduce(
        (total, defectNotice) => total + this.countActionRequiredDefects(defectNotice),
        0
      );
      return `${count}`;
    },
    assignedColor() {
      switch (this.defectAssignment.status) {
        case 'none':
          return 'grey';
        case 'some':
          return 'orange';
        case 'all':
          return 'green';
        default:
          return 'grey';
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.v-application .theme--light.v-data-table tbody tr {
  &.secondary:not(:last-child) {
    border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
  }
}

.title-icon {
  vertical-align: middle;
  margin-right: 14px;
}

.assignment-headline {
  font-size: 18px;
  font-weight: 500;
}

.assignment {
  font-size: 16px;
  padding-bottom: 0px;
  padding-top: 5px;
}

.hivis-headline {
  font-size: 16px;
  font-weight: 500;
  padding-bottom: 0;
}

.hivis-detail {
  font-size: 14px;
  padding-bottom: 4px;
  padding-top: 8px;
}
</style>

<style lang="scss">
td.shrink {
  width: 1px;
  white-space: nowrap !important;

  &.sortable {
    padding-right: 10px;
  }

  &.actions {
    text-align: right;
  }
}
</style>
